import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '../store/auth.store.ts';

/**
 * Axios 實例工廠
 * - 自動帶入 Authorization Bearer Token
 * - 401 時自動用 Refresh Token (HttpOnly Cookie) 換新 Access Token
 * - Refresh 也失敗才清除 session 跳轉登入頁
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: '/api',
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // 帶入 HttpOnly Cookie（Refresh Token）
  });

  type SessionRequest = InternalAxiosRequestConfig & {
    _retried?: boolean;
    _sessionRevision?: number;
    _socketRecovery?: boolean;
    _sessionToken?: string | null;
  };
  // Refresh work belongs to the login session that started it.
  let refreshPromise: Promise<string> | null = null;
  let refreshRevision: number | null = null;

  // Proactive token refresh — 在到期前 60 秒自動背景更新
  let proactiveTimer: ReturnType<typeof setTimeout> | null = null;
  let scheduledForToken: string | null = null;

  useAuthStore.subscribe((state, previous) => {
    if (state.sessionRevision !== previous.sessionRevision) {
      if (proactiveTimer) clearTimeout(proactiveTimer);
      proactiveTimer = null;
      scheduledForToken = null;
      refreshPromise = null;
      refreshRevision = null;
    }
  });

  function scheduleProactiveRefresh(token: string) {
    const revision = useAuthStore.getState().sessionRevision;
    if (scheduledForToken === token) return;
    scheduledForToken = token;
    if (proactiveTimer) clearTimeout(proactiveTimer);
    try {
      const payload = JSON.parse(atob(token.split('.')[1] ?? ''));
      const delay = payload.exp * 1000 - Date.now() - 60_000; // 60s before expiry
      if (delay <= 0) return;
      proactiveTimer = setTimeout(async () => {
        if (
          useAuthStore.getState().sessionRevision !== revision ||
          useAuthStore.getState().accessToken !== token
        )
          return;
        try {
          const res = await client.post<{ success: boolean; data: { accessToken: string } }>(
            '/auth/refresh',
            undefined,
            { _sessionRevision: revision } as AxiosRequestConfig,
          );
          if (
            useAuthStore.getState().sessionRevision !== revision ||
            useAuthStore.getState().accessToken !== token
          )
            return;
          const newToken = res.data.data.accessToken;
          useAuthStore.getState().updateAccessToken(newToken);
          scheduleProactiveRefresh(newToken);
        } catch {
          // Refresh failed — 401 interceptor will handle on next request
        }
      }, delay);
    } catch {
      // Invalid token format — skip
    }
  }

  // 請求攔截器：帶入 Access Token + 排程 proactive refresh
  client.interceptors.request.use((config) => {
    const { accessToken, sessionRevision } = useAuthStore.getState();
    const request = config as SessionRequest;
    if (request._sessionRevision !== undefined && request._sessionRevision !== sessionRevision) {
      throw new axios.CanceledError('Session changed');
    }
    request._sessionRevision = sessionRevision;
    request._sessionToken = accessToken;
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
      scheduleProactiveRefresh(accessToken);
    }
    return config;
  });

  // 回應攔截器：401 → 自動 refresh → 重送原請求
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { response, config } = error;
      const originalRequest = config as SessionRequest;
      const revision = originalRequest?._sessionRevision;
      const requestToken = originalRequest?._sessionToken;
      if (revision === undefined || revision !== useAuthStore.getState().sessionRevision) {
        return Promise.reject(error);
      }

      if (
        requestToken === useAuthStore.getState().accessToken &&
        !originalRequest._socketRecovery &&
        response?.status === 401 &&
        ['DEVICE_BINDING_REVOKED', 'DEVICE_BINDING_DENIED'].includes(response.data?.errorCode)
      ) {
        void recoverDeviceBinding(response.data.errorCode, false);
        return Promise.reject(error);
      }

      // 登入/refresh 端點的 401 屬於業務錯誤，不做自動 refresh
      if (requestToken !== useAuthStore.getState().accessToken) return Promise.reject(error);
      const isAuthEndpoint = originalRequest?.url?.startsWith('/auth/');
      if (response?.status !== 401 || isAuthEndpoint || originalRequest?._retried) {
        return Promise.reject(error);
      }

      // 標記已重試，避免無限迴圈
      originalRequest._retried = true;

      try {
        // 合併併發的 refresh 請求（只發一次）
        if (!refreshPromise || refreshRevision !== revision) {
          refreshRevision = revision;
          const pending = client
            .post<{ success: boolean; data: { accessToken: string } }>('/auth/refresh', undefined, {
              _sessionRevision: revision,
            } as AxiosRequestConfig)
            .then((res) => res.data.data.accessToken)
            .finally(() => {
              if (refreshPromise === pending) {
                refreshPromise = null;
                refreshRevision = null;
              }
            });
          refreshPromise = pending;
        }

        const newToken = await refreshPromise;
        if (
          useAuthStore.getState().sessionRevision !== revision ||
          ![requestToken, newToken].includes(useAuthStore.getState().accessToken)
        )
          return Promise.reject(error);
        useAuthStore.getState().updateAccessToken(newToken);
        scheduleProactiveRefresh(newToken);

        // 用新 token 重送原請求
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return client(originalRequest);
      } catch (refreshError) {
        if (
          useAuthStore.getState().sessionRevision !== revision ||
          requestToken !== useAuthStore.getState().accessToken ||
          !axios.isAxiosError(refreshError) ||
          refreshError.response?.status !== 401 ||
          (recoveringDevice?.revision === revision && recoveringDevice.token === requestToken)
        ) {
          return Promise.reject(error);
        }
        // Refresh 也失敗 → session 已無效，跳轉登入
        useAuthStore.getState().clearAuth();
        // Don't redirect if already on login page (prevents venue selection disruption)
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    },
  );

  return client;
};

export const apiClient = createApiClient();

/** 統一 API 呼叫包裝（自動提取 data.data） */
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<{ success: boolean; data: T }>(url, config).then((r) => r.data.data as T),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.post<{ success: boolean; data: T }>(url, data, config).then((r) => r.data.data as T),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.patch<{ success: boolean; data: T }>(url, data, config).then((r) => r.data.data as T),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.put<{ success: boolean; data: T }>(url, data, config).then((r) => r.data.data as T),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<{ success: boolean; data: T }>(url, config).then((r) => r.data.data as T),
};

let recoveringDevice: { revision: number; token: string | null } | null = null;

/** A socket route may cancel recovery without changing the login session (MFA setup). */
export async function recoverDeviceBinding(
  errorCode = 'DEVICE_BINDING_REVOKED',
  clearCookies = true,
  isCurrent: () => boolean = () => true,
) {
  const { sessionRevision: revision, accessToken: token } = useAuthStore.getState();
  if (!isCurrent() || (recoveringDevice?.revision === revision && recoveringDevice.token === token))
    return;
  const operation = { revision, token };
  recoveringDevice = operation;
  try {
    if (clearCookies && errorCode === 'DEVICE_BINDING_REVOKED')
      await apiClient.post('/auth/refresh', undefined, {
        timeout: 3000,
        _socketRecovery: true,
        _sessionRevision: revision,
      } as AxiosRequestConfig);
  } catch {
    // Revoked refresh responses clear HttpOnly cookies; no response detail is exposed.
  } finally {
    if (recoveringDevice === operation) recoveringDevice = null;
    const state = useAuthStore.getState();
    if (isCurrent() && state.sessionRevision === revision && state.accessToken === token) {
      state.clearAuth();
      window.location.href =
        '/login' + '?device=' + (errorCode === 'DEVICE_BINDING_REVOKED' ? 'revoked' : 'denied');
    }
  }
}

export type SocketRecoveryResult = 'refreshed' | 'rejected' | 'unavailable' | 'stale';

/** One bounded attempt. Only a current caller may change authentication. */
export async function recoverSocketSession(
  isCurrent: () => boolean,
): Promise<SocketRecoveryResult> {
  const { sessionRevision: revision, accessToken: token } = useAuthStore.getState();
  const current = () =>
    isCurrent() &&
    useAuthStore.getState().sessionRevision === revision &&
    useAuthStore.getState().accessToken === token;
  if (!current()) return 'stale';
  try {
    const response = await apiClient.post<{ data: { accessToken: string } }>(
      '/auth/refresh',
      undefined,
      { timeout: 3000, _socketRecovery: true, _sessionRevision: revision } as AxiosRequestConfig,
    );
    if (!current()) return 'stale';
    if (!response.data.data.accessToken) return 'unavailable';
    useAuthStore.getState().updateAccessToken(response.data.data.accessToken);
    return 'refreshed';
  } catch (error) {
    if (!current()) return 'stale';
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      useAuthStore.getState().clearAuth();
      window.location.href = '/login' + '?session=expired';
      return 'rejected';
    }
    return 'unavailable';
  }
}
